import { IUserData } from "@/types";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}
export const hexToRgb = (hex: string): [number, number, number] => {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
};
function componentToHex(c: number): string {
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
}

export function colorToHsl(color: string): string {
    const isGradient =
        color?.includes("radial-gradient") ||
        color?.includes("linear-gradient");
    if (isGradient) {
        return color;
    }
    // Check if input is in rgb() format
    const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
    const match = color.match(rgbRegex);

    if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        // Normalize RGB values to [0, 1]
        const rNorm = r / 255;
        const gNorm = g / 255;
        const bNorm = b / 255;

        // Find the max and min RGB values
        const max = Math.max(rNorm, gNorm, bNorm);
        const min = Math.min(rNorm, gNorm, bNorm);
        const delta = max - min;

        // Calculate Hue
        let h = 0;
        if (delta !== 0) {
            if (max === rNorm) {
                h = (gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0);
            } else if (max === gNorm) {
                h = (bNorm - rNorm) / delta + 2;
            } else {
                h = (rNorm - gNorm) / delta + 4;
            }
            h *= 60;
        }

        // Calculate Lightness
        const l = (max + min) / 2;

        // Calculate Saturation
        let s = 0;
        if (delta !== 0) {
            s = delta / (1 - Math.abs(2 * l - 1));
        }

        return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    } else {
        // If it's not RGB, assume it's a HEX color code
        // Remove '#' if it's there
        color = color.replace(/^#/, "");

        // Parse the hex into RGB components
        let r: number = parseInt(color.slice(0, 2), 16);
        let g: number = parseInt(color.slice(2, 4), 16);
        let b: number = parseInt(color.slice(4, 6), 16);

        // Normalize RGB values to [0, 1]
        r /= 255;
        g /= 255;
        b /= 255;

        // Find the max and min RGB values
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;

        // Calculate Hue
        let h = 0;
        if (delta !== 0) {
            if (max === r) {
                h = (g - b) / delta + (g < b ? 6 : 0);
            } else if (max === g) {
                h = (b - r) / delta + 2;
            } else {
                h = (r - g) / delta + 4;
            }
            h *= 60;
        }

        // Calculate Lightness
        const l = (max + min) / 2;

        // Calculate Saturation
        let s = 0;
        if (delta !== 0) {
            s = delta / (1 - Math.abs(2 * l - 1));
        }

        return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    }
}
export function colorToHex(color: string): string {
    const isGradient =
        color?.includes("radial-gradient") ||
        color?.includes("linear-gradient");
    if (isGradient) {
        return color;
    }

    // Check if input is in rgb() format
    const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
    let match = color.match(rgbRegex);

    if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        return rgbToHexNew(r, g, b);
    }

    // Check if input is in hsl() format
    const hslRegex = /^hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)$/;
    match = color.match(hslRegex);

    if (match) {
        const h = parseInt(match[1]);
        const s = parseInt(match[2]) / 100;
        const l = parseInt(match[3]) / 100;
        return hslToHex(h, s, l);
    }

    return color; // Assume it's already a HEX code if it doesn't match RGB or HSL
}
export function rgbToHex(rgb: string): string {
    const values = rgb.match(/\d+/g);
    if (!values || values.length !== 3) {
        return rgb.replace("#", "");
    }

    const r = parseInt(values[0]);
    const g = parseInt(values[1]);
    const b = parseInt(values[2]);

    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

function rgbToHexNew(r: number, g: number, b: number): string {
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

function hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
        g = 0,
        b = 0;
    if (h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        g = 0;
        b = c;
    } else {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return rgbToHexNew(r, g, b);
}

const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
export function getTextColorForBackground(background: string): string {
    const isGradient =
        background?.includes("radial-gradient") ||
        background?.includes("linear-gradient");
    if (isGradient) {
        // Handle gradient background
        const rgbStops =
            background.match(/rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)/g) ||
            [];

        const hexStops = rgbStops.map((rgb) => {
            // Extract RGB values using regex
            const [, r, g, b] = rgb
                .match(/(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})/)!
                .map(Number);

            // Convert each component to a two-digit hex value
            const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;

            return hex;
        });
        const colorStops = background.match(/#[0-9A-Fa-f]{6}/g) || hexStops;
        if (colorStops.length === 0) {
            // No color stops found, default to a text color (e.g., black or white)
            return "black"; // Use black text by default
        }

        const totalLuminance = (colorStops as string[]).reduce(
            (acc: number, color: string) => {
                const [r, g, b]: any = rgbRegex.test(color)
                    ? rgbRegex.exec(color)?.slice(1).map(Number)
                    : hexToRgb(color);
                return acc + (0.299 * r + 0.587 * g + 0.114 * b);
            },
            0
        );

        const averageLuminance = totalLuminance / colorStops.length;
        if (averageLuminance > 128) {
            return "black"; // Use black text for light gradients
        } else {
            return "white"; // Use white text for dark gradients
        }
    } else {
        // Handle solid background color
        //check if it is rgb already
        const [r, g, b]: any = rgbRegex.test(background)
            ? rgbRegex.exec(background)?.slice(1).map(Number)
            : hexToRgb(background);
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        if (luminance > 128) {
            return "black"; // Use black text for light backgrounds
        } else {
            return "white"; // Use white text for dark backgrounds
        }
    }
}
export function generateCustomAsset(title: string): string {
    return (
        "CustomAsset" +
        title
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join("")
    );
}
export function isUUID(uuid: string) {
    const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid.split(".")[0]);
}

export function shadow(value: any) {
    switch (value) {
        case 0:
            return null;
        case 25:
            return "shadow-sm";
        case 50:
            return "shadow-md";
        case 50:
            return "shadow-lg";
        case 75:
            return "shadow-xl";
        case 100:
    }
}
export function startsWithHTTPS(str: string) {
    return str.startsWith("https://");
}
export function replaceProtocolWithHTTPS(url: string) {
    // Match and remove any existing protocol (e.g., http://, https://, wss://, etc.)
    url = url.replace(/^[a-zA-Z]+:\/\//, "");
    // Prepend https:// to the sanitized URL
    return `https://${url}`;
}
export function addImportantToStyles(css: string): string {
    const importRules = css.match(/@import[^;]+;/g) || [];
    const keyframesRules =
        css.match(/@keyframes\s+[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g) || [];
    const otherRules = css
        .replace(/@import[^;]+;/g, "")
        .replace(/@keyframes\s+[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g, "");

    const otherRulesWithImportant = otherRules.replace(
        /([^;]+);(?=[^}]*\})/g,
        "$1 !important;"
    );

    const keyframesRulesWithoutImportant = keyframesRules.map((rule) =>
        rule.replace(/\s*!important\s*;/g, ";")
    );

    return [
        ...importRules,
        ...keyframesRulesWithoutImportant,
        otherRulesWithImportant,
    ].join("\n");
}

export const brandVariantIds: { [key: string]: number } = {
    // //Temp
    // bplus: 419529,
    // bpro: 419530,
    bplus: 215344,
    bpro: 410772,
};
export const brandVariantURLID = [
    {
        type: "bplus",
        variant: brandVariantIds.bplus,
        url_id:
            "5186954a-857c-459b-abd2-ef81b60d0b95?enabled=" +
            brandVariantIds.bplus,
    },
    {
        type: "bpro",
        variant: brandVariantIds.bpro,
        url_id:
            "848065e9-6d45-4e6f-add2-d3347fd80e0e?enabled=" +
            brandVariantIds.bpro,
    },
];

export function titleToSlug(title: string): string {
    return (
        title
            .toLowerCase() // Convert to lowercase
            // .trim() // Remove leading/trailing spaces
            .replace(/[^a-z0-9\s-.]/g, "") // Allow letters, numbers, spaces, dashes, and periods
            .replace(/^-|-$/g, "") // Remove leading/trailing dashes
            .replace(/\s+/g, "-")
    ); // Replace spaces with dashes
}

export const checkFeatureFlag = (
    featureFlag: string,
    account: IUserData,
    actor?: IUserData | null | undefined
) => {
    return account.feature_flags[featureFlag]
        ? account.feature_flags[featureFlag]
        : actor?.feature_flags[featureFlag] || false;
};

export const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};

export function getColorFromUuid(uuid: string): string {
    // 1. Remove hyphens from the UUID.
    const strippedUuid = uuid.replace(/-/g, "");

    // 2. Take the first 6 characters (or however many you want for your color).
    const colorHex = strippedUuid.substring(0, 6);

    // 3. Ensure the hex code is valid (pad with '0' if needed).
    const paddedColorHex = colorHex.padEnd(6, "0");

    // 4. Return the hex code with the '#' prefix.
    return `#${paddedColorHex}`;
}
